// 同じ商品を店ごとの単価にまとめて、どこが安いかを見せるカード
import { format } from 'date-fns'
import { Trophy } from 'lucide-react'

import type { PriceBasis, PriceStats, PriceSummary, PriceSummaryStore } from '@/api/types/receipt'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

import { formatAmount } from '../field-value'

type Props = {
  summary: PriceSummary
  basis: PriceBasis
}

const statsOf = (source: { list_price: PriceStats; net_price: PriceStats }, basis: PriceBasis): PriceStats =>
  basis === 'net' ? source.net_price : source.list_price

const storeLabel = (store: PriceSummaryStore): string => store.store_name || store.place_name || '店名不明'

const formatLastPurchase = (iso: string | null): string => {
  if (!iso) return '日付不明'
  return format(parseUtcDate(iso), 'M/d')
}

export const PriceComparisonCard = ({ summary, basis }: Props) => {
  if (summary.item_count === 0) return null

  const overall = statsOf(summary, basis)

  return (
    <div className="rounded border border-gray-700 p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="text-sm font-medium text-foreground">
          最安 <span className="tabular-nums">{formatAmount(overall.min)}</span>
        </span>
        <span>平均 {formatAmount(overall.avg)}</span>
        <span>
          {summary.stores.length}店 · {summary.item_count}回
        </span>
      </div>

      <div className="mt-2 flex flex-col gap-1">
        {summary.stores.map((store) => {
          const stats = statsOf(store, basis)
          const isCheapest = stats.min !== null && stats.min === overall.min
          return (
            <div key={storeLabel(store)} className={cn('rounded px-2 py-1.5', isCheapest && 'bg-amber-500/10')}>
              <div className="flex items-baseline gap-2 text-sm">
                {isCheapest && <Trophy className="size-3.5 shrink-0 self-center text-amber-500" />}
                <span className="min-w-0 flex-1 wrap-break-word">{storeLabel(store)}</span>
                <span className="shrink-0 font-medium tabular-nums">{formatAmount(stats.min)}</span>
              </div>
              <div className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                平均 {formatAmount(stats.avg)} · {store.purchase_count}回 ·{' '}
                {formatLastPurchase(store.last_purchased_at)}
              </div>
            </div>
          )
        })}
      </div>

      {summary.matched_count > summary.item_count && (
        <p className="mt-2 text-xs text-muted-foreground">
          金額が読めなかった{summary.matched_count - summary.item_count}件は集計に入れていません。
        </p>
      )}
    </div>
  )
}
